/**
 * KAN-826: data and state of the client companies: the list page (search,
 * pagination), the company card (requisites editor with field errors) and
 * linking a client card to a company. Components only render what these
 * hooks return.
 */
import { useCallback, useEffect, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { usePageSize, type PageSizeState } from '@/hooks/usePageSize';
import { useDebounce } from '@/hooks/useDebounce';
import { useCanCallApi } from '@/hooks/useApiAccess';
import { ANY_UUID } from '@/lib/managerAccess';
import { fieldErrorMap, formatFieldErrors } from '@/lib/fieldErrors';
import { clientCompaniesApi } from '@/services/api/clientCompanies';
import { clientsApi } from '@/services/api/clients';
import { clientQueryKey } from '@/services/api/clientQueries';
import type {
  ClientCompany,
  ClientCompanyDetail,
  ClientCompanyUpdatePayload,
} from '@/types/clientCompany';

/** Prefix of every client companies query (list, search, card). */
export const CLIENT_COMPANIES_QUERY_KEY = ['client-companies'] as const;
const detailKey = (id: string | undefined) => [...CLIENT_COMPANIES_QUERY_KEY, 'detail', id] as const;

const SEARCH_DEBOUNCE_MS = 300;
const PICKER_PAGE_SIZE = 10;

/* ------------------------------ list page ------------------------------ */

export function useClientCompaniesPage() {
  const paging: PageSizeState = usePageSize('clientCompanies');
  const { setPage } = paging;
  const [search, setSearchState] = useState('');
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);

  const setSearch = useCallback(
    (value: string) => {
      setSearchState(value);
      setPage(1);
    },
    [setPage]
  );

  const params = { search: debouncedSearch, page: paging.page, page_size: paging.pageSize };
  const list = useQuery({
    queryKey: [...CLIENT_COMPANIES_QUERY_KEY, 'list', params],
    queryFn: () => clientCompaniesApi.list(params),
    placeholderData: keepPreviousData,
  });

  return { search, setSearch, paging, list };
}

/* ------------------------------ company card ------------------------------ */

export function useClientCompany(id: string | undefined) {
  return useQuery<ClientCompanyDetail>({
    queryKey: detailKey(id),
    queryFn: () => clientCompaniesApi.get(id as string),
    enabled: !!id,
  });
}

export type CompanyTextField = 'name' | 'inn' | 'ogrn' | 'address' | 'city' | 'region' | 'website';
export type CompanyListField = 'phones' | 'emails';

export interface ClientCompanyForm {
  name: string;
  inn: string;
  ogrn: string;
  address: string;
  city: string;
  region: string;
  website: string;
  phones: string[];
  emails: string[];
}

const FIELD_LABELS: Record<string, string> = {
  name: 'Название',
  inn: 'ИНН',
  ogrn: 'ОГРН',
  address: 'Адрес',
  city: 'Город',
  region: 'Регион',
  phones: 'Телефоны',
  emails: 'Почты',
  website: 'Сайт',
};

function formFrom(company: ClientCompany): ClientCompanyForm {
  return {
    name: company.name ?? '',
    inn: company.inn ?? '',
    ogrn: company.ogrn ?? '',
    address: company.address ?? '',
    city: company.city ?? '',
    region: company.region ?? '',
    website: company.website ?? '',
    phones: [...(company.phones ?? [])],
    emails: [...(company.emails ?? [])],
  };
}

const cleanList = (items: string[]): string[] => items.map((item) => item.trim()).filter(Boolean);

/** PATCH body: every requisite, list rows trimmed, empty rows left out. */
export function buildCompanyPayload(form: ClientCompanyForm): ClientCompanyUpdatePayload {
  return {
    name: form.name,
    inn: form.inn,
    ogrn: form.ogrn,
    address: form.address,
    city: form.city,
    region: form.region,
    phones: cleanList(form.phones),
    emails: cleanList(form.emails),
    website: form.website,
  };
}

/** Requisites editor of the company card: form state, field errors, save. */
export function useClientCompanyEditor(id: string | undefined, company: ClientCompanyDetail | undefined) {
  const queryClient = useQueryClient();
  const canEdit = useCanCallApi('PATCH', `/api/v1/client-companies/${id ?? ANY_UUID}/`);
  const [form, setForm] = useState<ClientCompanyForm | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // The form follows the saved company (load and a successful save).
  useEffect(() => {
    if (company) setForm(formFrom(company));
  }, [company]);

  const setField = useCallback((field: CompanyTextField, value: string) => {
    setForm((current) => (current ? { ...current, [field]: value } : current));
  }, []);

  const setListItem = useCallback((field: CompanyListField, index: number, value: string) => {
    setForm((current) =>
      current ? { ...current, [field]: current[field].map((item, i) => (i === index ? value : item)) } : current
    );
  }, []);

  const addListItem = useCallback((field: CompanyListField) => {
    setForm((current) => (current ? { ...current, [field]: [...current[field], ''] } : current));
  }, []);

  const removeListItem = useCallback((field: CompanyListField, index: number) => {
    setForm((current) => (current ? { ...current, [field]: current[field].filter((_, i) => i !== index) } : current));
  }, []);

  const mutation = useMutation({
    mutationFn: (payload: ClientCompanyUpdatePayload) => clientCompaniesApi.update(id as string, payload),
    onMutate: () => setErrors({}),
    onSuccess: (saved) => {
      queryClient.setQueryData(detailKey(id), saved);
      void queryClient.invalidateQueries({ queryKey: CLIENT_COMPANIES_QUERY_KEY });
      toast.success('Организация сохранена');
    },
    onError: (err) => {
      setErrors(fieldErrorMap(err));
      toast.error(formatFieldErrors(err, FIELD_LABELS, 'Не удалось сохранить организацию'));
    },
  });

  const save = useCallback(() => {
    if (form) mutation.mutate(buildCompanyPayload(form));
  }, [form, mutation]);

  return {
    form,
    errors,
    canEdit,
    setField,
    setListItem,
    addListItem,
    removeListItem,
    save,
    isSaving: mutation.isPending,
  };
}

export type ClientCompanyEditor = ReturnType<typeof useClientCompanyEditor>;

/* ------------------------------ client card field ------------------------------ */

/** Companies matching the typed text (picker of the client card). */
export function useClientCompanySearch(search: string, enabled: boolean) {
  const debounced = useDebounce(search.trim(), SEARCH_DEBOUNCE_MS);
  const canSearch = useCanCallApi('GET', '/api/v1/client-companies/');
  const params = { search: debounced, page: 1, page_size: PICKER_PAGE_SIZE };
  const query = useQuery({
    queryKey: [...CLIENT_COMPANIES_QUERY_KEY, 'search', params],
    queryFn: () => clientCompaniesApi.list(params),
    enabled: enabled && canSearch && debounced !== '',
  });
  return { ...query, debounced, canSearch };
}

export type ClientCompanyLinkPayload = { client_company_id: string | null } | { company: string };

/** Link / unlink / create-and-link the company of a client card (PATCH of the client). */
export function useLinkClientCompany(clientId: string) {
  const queryClient = useQueryClient();
  const canEdit = useCanCallApi('PATCH', `/api/v1/clients/${clientId || ANY_UUID}/`);
  const mutation = useMutation({
    mutationFn: (payload: ClientCompanyLinkPayload) => clientsApi.update(clientId, payload),
    onSuccess: () => toast.success('Организация клиента сохранена'),
    onError: (err) =>
      toast.error(
        formatFieldErrors(
          err,
          { client_company_id: 'Организация', company: 'Организация' },
          'Не удалось сохранить организацию клиента'
        )
      ),
    onSettled: () => {
      for (const queryKey of [clientQueryKey(clientId), ['clients'], CLIENT_COMPANIES_QUERY_KEY]) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
  return { ...mutation, canEdit };
}
